import { useTheme } from "@amx/ux-twoui-theming";
import { Text as FluentText } from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../shared/logging";
import { TextBaseProps, validateProps } from "../../shared/props";
import { FontWeightOverride, getFontRampStyle, TextTypes } from "../../styles/fonts";

export interface TextProps extends TextBaseProps {
    /**
     * Style (size/weight/color) for the component, see figma in readme for
     * reference
     */
    readonly variant?: TextTypes;
    /**
     * Optional override of the font-weight defined in the type ramp
     */
    readonly weightOverride?: FontWeightOverride;
    readonly children: React.ReactNode;

    /** Optional flag to switch the Text to a "disabled" variant */
    readonly disabled?: boolean;
}

export const Text: React.FC<TextProps> = ({
    block,
    nowrap,
    variant = "bodyPrimaryText",
    colorOverride,
    weightOverride,
    children,
    disabled = false,
    ...otherProps
}) => {
    useComponentLogger("Text", otherProps.id);

    validateProps("Text", otherProps);
    const theme = useTheme();

    const styles = getFontRampStyle(theme, variant, weightOverride, disabled ? "disabled" : colorOverride);

    return (
        <FluentText
            block={block}
            nowrap={nowrap}
            styles={{
                root: {
                    fontSize: styles.fontSize,
                    fontWeight: styles.fontWeight,
                    lineHeight: styles.lineHeight,
                    fontFamily: styles.fontFamily,
                    selectors: styles.selectors,
                    color: styles.color
                }
            }}
            {...otherProps}
        >
            {children}
        </FluentText>
    );
};
